import { useState } from "react";
import { useGoalPinPortfolioMutation } from "web_myplann/Api/Goals/useGoalPinPortfolioMutation";
import { useGoalUnpinPortfolioMutation } from "web_myplann/Api/Goals/useGoalUnpinPortfolioMutation";
import { ALLOCATION_MAX, ALLOCATION_MIN } from "web_myplann/Goals/constants";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	type GoalPortfolioRequestDTO,
	type GoalPortfolioResponseDTO,
	GoalPortfolioTypeEnum,
	type UpdateGoalRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";

type UseGoalPortfolioActionsProps = {
	goalId: number;
	goalDto: GoalDetailResponseDTO | undefined;
	onPinSettled?: () => void;
};

const mapPortfolioToRequest = (
	p: GoalPortfolioRequestDTO | GoalPortfolioResponseDTO,
): GoalPortfolioRequestDTO => ({
	portfolioId: p.portfolioId,
	usagePercent: p.usagePercent,
	type: p.type,
});

const mapUpdateBody = (
	dto: GoalDetailResponseDTO,
	portfolios: GoalPortfolioRequestDTO[],
): UpdateGoalRequestDTO => ({
	name: dto.name,
	investmentHorizonEndDate: dto.targetDate,
	currencyCode: dto.currencyCode,
	oneTimePayment: dto.oneTimePayment,
	periodicPayment: dto.periodicPayment,
	portfolios,
});

export const useGoalPortfolioActions = ({
	goalId,
	goalDto,
	onPinSettled,
}: UseGoalPortfolioActionsProps) => {
	const [unpinningId, setUnpinningId] = useState<number | null>(null);
	const { mutate: pinPortfolio } = useGoalPinPortfolioMutation();
	const { mutate: unpinPortfolio } = useGoalUnpinPortfolioMutation();

	const pin = (
		picked: GoalPortfolioListItemResponseDTO,
		type: GoalPortfolioTypeEnum,
	) => {
		if (!goalDto) return;

		const existing = goalDto.portfolios ?? [];
		const alreadyPinned = existing.some((p) => p.portfolioId === picked.id);
		const isSetAside = type === GoalPortfolioTypeEnum.SetAside;
		const available = Math.max(0, ALLOCATION_MAX - picked.usagePercent);
		if (alreadyPinned || (!isSetAside && available < ALLOCATION_MIN)) {
			onPinSettled?.();
			return;
		}

		pinPortfolio(
			{
				id: goalId,
				body: mapUpdateBody(goalDto, [
					...existing.map(mapPortfolioToRequest),
					{
						portfolioId: picked.id,
						usagePercent: isSetAside ? ALLOCATION_MAX : available,
						type,
					},
				]),
			},
			{ onSettled: () => onPinSettled?.() },
		);
	};

	const unpin = (portfolioId: number) => {
		if (!goalDto) return;

		setUnpinningId(portfolioId);
		unpinPortfolio(
			{
				id: goalId,
				body: mapUpdateBody(
					goalDto,
					(goalDto.portfolios ?? [])
						.filter((p) => p.portfolioId !== portfolioId)
						.map(mapPortfolioToRequest),
				),
			},
			{ onSettled: () => setUnpinningId(null) },
		);
	};

	const editAllocation = (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => {
		if (!goalDto) return;

		const updated = (goalDto.portfolios ?? []).map((p) =>
			p.portfolioId === portfolioId
				? { portfolioId: p.portfolioId, usagePercent, type: p.type }
				: mapPortfolioToRequest(p),
		);

		pinPortfolio(
			{
				id: goalId,
				body: mapUpdateBody(goalDto, updated),
			},
			{ onSettled: () => options?.onSettled?.() },
		);
	};

	return { pin, unpin, editAllocation, unpinningId };
};
